import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";

// Bulk import Shopify orders by order number list. Paste-friendly:
// accepts numbers separated by commas, newlines, or spaces. Ignores '#'.
export default function ShopifyBulkImportDialog({ initialNumbers = "", onImported }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [raw, setRaw] = useState(initialNumbers);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("csv"); // 'csv' | 'numbers'
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let ok = true;
    api.get("/shopify/bulk-import/status")
      .then((r) => { if (ok) setStatus(r.data); })
      .catch(() => {});
    return () => { ok = false; };
  }, [open]);

  const parsed = React.useMemo(() => {
    const nums = (raw || "")
      .split(/[\s,]+/)
      .map((s) => s.replace(/^#/, "").trim())
      .filter((s) => /^\d+$/.test(s));
    return Array.from(new Set(nums));
  }, [raw]);

  const configured = status?.configured === true;

  const submit = async () => {
    if (mode === "csv") {
      if (!file) {
        toast.error("Choose a CSV file first");
        return;
      }
      setSaving(true);
      setResult(null);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/shopify/import-csv", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setResult({ ...r.data, mode: "csv" });
        const created = r.data.imported?.length || 0;
        const dup = r.data.duplicates?.length || 0;
        const err = r.data.errors?.length || 0;
        toast.success(`Imported ${created} · Duplicates ${dup}${err ? ` · Errors ${err}` : ""}`);
        if (typeof onImported === "function") {
          try { await onImported(r.data); } catch (_) { /* ignore */ }
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "CSV import failed");
      } finally {
        setSaving(false);
      }
      return;
    }

    // mode === 'numbers' (Admin API)
    if (!parsed.length) {
      toast.error("Paste at least one order number");
      return;
    }
    setSaving(true);
    setResult(null);
    try {
      const r = await api.post("/shopify/bulk-import", { order_numbers: parsed });
      setResult({ ...r.data, mode: "numbers" });
      const created = r.data.imported?.length || 0;
      const dup = r.data.duplicates?.length || 0;
      const missing = r.data.not_found_in_shopify?.length || 0;
      toast.success(
        `Imported ${created} · Duplicates ${dup} · Not found in Shopify ${missing}`
      );
      if (typeof onImported === "function") {
        try { await onImported(r.data); } catch (_) { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="shopify-bulk-import-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          🛍 Import from Shopify
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="shopify-bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Orders from Shopify</DialogTitle>
          <DialogDescription>
            Two ways to pull your Shopify orders into Leprecon+ as Pre Orders.
            Orders already imported (by Shopify ID) are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Mode switcher */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <button
            type="button"
            data-testid="shopify-bulk-mode-csv"
            onClick={() => { setMode("csv"); setResult(null); }}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-md ${
              mode === "csv" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            📄 Upload CSV
          </button>
          <button
            type="button"
            data-testid="shopify-bulk-mode-numbers"
            onClick={() => { setMode("numbers"); setResult(null); }}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-md ${
              mode === "numbers" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            🔢 Paste Order Numbers (needs API token)
          </button>
        </div>

        {mode === "numbers" && !configured && (
          <div
            className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3 text-sm"
            data-testid="shopify-bulk-not-configured"
          >
            <div className="font-semibold mb-1">⚠ Shopify Admin API not connected yet.</div>
            <div className="text-xs">
              Paste a <code className="font-mono">shpat_…</code> token into{" "}
              <code className="font-mono">backend/.env</code> as{" "}
              <code className="font-mono">SHOPIFY_ADMIN_ACCESS_TOKEN</code>, or use the CSV tab
              above which works without a token.
            </div>
          </div>
        )}

        <div className="space-y-3">
          {mode === "csv" ? (
            <div>
              <Label className="text-xs">Shopify Orders CSV</Label>
              <div className="text-[11px] text-slate-500 mb-2">
                Export in Shopify Admin → <b>Orders</b> → <b>Export</b> → <b>Plain CSV file</b>. Every order in the file will be imported as a Pre Order.
              </div>
              <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-4 flex items-center justify-between gap-3">
                <div className="text-sm font-mono text-slate-600 truncate">
                  {file ? `📄 ${file.name} · ${(file.size / 1024).toFixed(1)} KB` : "No file chosen"}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="shopify-csv-file"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="shopify-csv-pick"
                  className="border-slate-900 text-slate-900"
                >
                  {file ? "Change file" : "Choose CSV"}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Order numbers (comma / newline separated · '#' optional)</Label>
              <Textarea
                data-testid="shopify-bulk-input"
                rows={8}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="e.g. 4802, 4842, 4853, 4854&#10;4857 4860 4861 4862&#10;#4867"
                className="font-mono text-sm"
              />
              <div className="text-xs text-slate-500 mt-1 font-mono">
                {parsed.length} valid order number{parsed.length === 1 ? "" : "s"} detected
              </div>
            </div>
          )}

          {result && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm space-y-2" data-testid="shopify-bulk-result">
              <div className="font-semibold text-emerald-900">Import complete</div>
              <div className="text-xs font-mono grid grid-cols-3 gap-2">
                <div>
                  <div className="uppercase tracking-widest text-emerald-700">Imported</div>
                  <div className="text-lg font-bold text-emerald-900">{result.imported?.length || 0}</div>
                </div>
                <div>
                  <div className="uppercase tracking-widest text-amber-700">Already existed</div>
                  <div className="text-lg font-bold text-amber-900">{result.duplicates?.length || 0}</div>
                </div>
                <div>
                  <div className="uppercase tracking-widest text-red-700">
                    {result.mode === "csv" ? "Errors" : "Not found"}
                  </div>
                  <div className="text-lg font-bold text-red-900">
                    {result.mode === "csv"
                      ? (result.errors?.length || 0)
                      : (result.not_found_in_shopify?.length || 0)}
                  </div>
                </div>
              </div>
              {result.mode !== "csv" && (result.not_found_in_shopify?.length || 0) > 0 && (
                <div className="text-xs font-mono text-slate-600 pt-2 border-t border-emerald-200">
                  <div className="text-slate-500 mb-1">Not found in Shopify (may not exist or beyond {result.pages_scanned}-page scan):</div>
                  <div className="max-h-24 overflow-y-auto">{result.not_found_in_shopify.join(", ")}</div>
                </div>
              )}
              {(result.errors?.length || 0) > 0 && (
                <div className="text-xs font-mono text-red-700 pt-2 border-t border-emerald-200">
                  <div className="mb-1">Errors:</div>
                  {result.errors.map((e, i) => (
                    <div key={i}>#{e.order_number} — {e.message}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="shopify-bulk-cancel"
            >
              Close
            </Button>
            <Button
              onClick={submit}
              disabled={
                saving ||
                (mode === "numbers" && (!configured || !parsed.length)) ||
                (mode === "csv" && !file)
              }
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="shopify-bulk-submit"
            >
              {saving
                ? "Importing…"
                : mode === "csv"
                  ? (file ? "Import CSV" : "Choose a file")
                  : (configured
                      ? `Import ${parsed.length} order${parsed.length === 1 ? "" : "s"}`
                      : "Not Connected")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
